import React, { useLayoutEffect, useRef } from 'react';
import Button from './Button';
import ThemeToggle from './ThemeToggle';
import useSectionAnimation from 'hooks/animations/useSectionAnimation';
import VectorLoader from './VectorLoader';
import useMediaQuery from 'hooks/useMediaQuery';
import { useNavigate } from 'react-router-dom';
import { renderWithLineBreaks } from 'utils/renderWithLineBreaks';

const HeroSection = ({
  imageUrl,
  title,
  titleHighlight,
  subtitle,
  description,
  buttonText,
  showThemeToggle = false,
  loaderProgress,
  isLoading,
  buttonLink,
}) => {
  const backgroundRef = useRef(null);
  const titleRef = useRef(null);
  const descriptionRef = useRef(null);
  const buttonRef = useRef(null);
  const titleRowRef = useRef(null);
  const titleTextRef = useRef(null);
  const descriptionColumnRef = useRef(null);
  const isMobileSize = useMediaQuery();
  const navigate = useNavigate();

  const { backgroundImageUrl } = useSectionAnimation({
    backgroundRef,
    titleRef,
    descriptionRef,
    buttonRef,
    imageUrl,
    isLoading,
  });

  useLayoutEffect(() => {
    const titleEl = titleTextRef.current;
    const row = titleRowRef.current;
    if (!titleEl || !row) return;

    const fit = () => {
      titleEl.style.fontSize = '';
      const style = getComputedStyle(titleEl);
      const maxFontSize = parseFloat(style.fontSize);
      const paddingLeft = parseFloat(style.paddingLeft);
      const paddingRight = parseFloat(style.paddingRight);
      const titleRect = titleEl.getBoundingClientRect();
      const textLeft = titleRect.left + paddingLeft;
      const textWidth = titleEl.scrollWidth - paddingLeft - paddingRight;

      const rowRect = row.getBoundingClientRect();
      const descriptionColumn = descriptionColumnRef.current;
      const availableRight =
        isMobileSize || !descriptionColumn
          ? rowRect.right - paddingRight
          : descriptionColumn.getBoundingClientRect().left -
            rowRect.width * 0.025;
      const availableWidth = availableRight - textLeft;

      if (textWidth > availableWidth && availableWidth > 0) {
        titleEl.style.fontSize = `${Math.floor(
          (maxFontSize * availableWidth) / textWidth,
        )}px`;
      }
    };

    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(row);
    document.fonts?.ready.then(fit);

    return () => observer.disconnect();
  }, [title, titleHighlight, isMobileSize]);

  return (
    <div className='relative background-section-container overflow-hidden full-pager'>
      <div
        className='full-height full-width background-section absolute'
        style={{
          backgroundImage: backgroundImageUrl
            ? `url(${backgroundImageUrl})`
            : 'none',
        }}
        ref={backgroundRef}
      />
      <div className='hero-section full-height full-width flex flex-column z-1'>
        <div className='half-hero z-1 flex items-end hero-content items-end'>
          {subtitle && !isMobileSize && (
            <p
              className='paragraph-font-grotesk hero-subtitle font-white-color font-size-14 fp-p'
              ref={titleRef}
            >
              {subtitle}
            </p>
          )}
        </div>
        <div
          className='full-width flex items-center relative'
          ref={titleRowRef}
        >
          <p
            ref={(el) => {
              titleRef.current = el;
              titleTextRef.current = el;
            }}
            className={`img-page-font-grotesk font-white-color first-page-title-fs page-wrapper-x ${
              isMobileSize ? '' : 'absolute'
            }`}
          >
            {renderWithLineBreaks(title)} <br />
            {titleHighlight && (
              <span className='font-secondary'>
                {renderWithLineBreaks(titleHighlight)}
              </span>
            )}
          </p>
          <VectorLoader className='absolute' progress={loaderProgress} />
        </div>
        <div className='half-hero z-1 hero-content'>
          <div
            className='flex flex-column hero-section-des'
            ref={descriptionColumnRef}
          >
            {isMobileSize && (
              <p
                className='paragraph-font-grotesk hero-subtitle font-white-color font-size-14 fp-p'
                ref={titleRef}
              >
                {subtitle}
              </p>
            )}
            {!isMobileSize && (
              <p
                ref={descriptionRef}
                className='paragraph-font-grotesk font-white-color'
              >
                {description}
              </p>
            )}
            {buttonText && buttonLink && (
              <Button ref={buttonRef} onClick={() => navigate(buttonLink)}>
                {buttonText}
              </Button>
            )}
            {showThemeToggle && (
              <ThemeToggle
                className={`${
                  isMobileSize ? '' : 'absolute hero-theme-toggle'
                }`}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;
